import { FC, useCallback, useEffect, useMemo } from 'react';
import { useAtom } from 'jotai';

import type { TableState } from 'components/data-table/types';
import { useBlobDownload } from 'lib/hooks/useBlobDownload';
import { DEFAULT_PAGE_SIZE } from 'utils/page-sizes';

import { createReadOnlyDraftSlotsQueryAtoms } from '../atoms';
import { SlotTable } from '../slot-table';
import type { FlatSlotRow } from '../types';
import {
  draftSlotFilterStateToParams,
  flattenDraftSlots,
  sortParamsFromState,
} from '../utils';

/** Props for {@link Step2SlotsTable}. */
export interface Step2SlotsTableProps {
  /** Active draft id. The fetch fires on mount/draftId-change. */
  draftId: string;
}

/**
 * This table's own read-only slots query (see `createReadOnlyDraftSlotsQueryAtoms`).
 */
const [, draftSlotsReadOnlyQueryLoadableAtom] =
  createReadOnlyDraftSlotsQueryAtoms();

/**
 * Step 2 read-only slots table. Shows only allocations currently selected
 * on the draft (BE-filtered via `selected: true`). No selection column,
 * no pin column, no PATCH wiring — review-screen presentation only.
 *
 * Reuses {@link SlotTable} for columns, expandable subtract sub-row, and
 * the DataTable shell. Verification (NRI/WI) cells stay interactive — that
 * state lives in `verificationStatesAtom` which is conceptually a per-slot
 * annotation, not a selection signal.
 *
 * @param {Step2SlotsTableProps} props - draft id
 * @return {JSX.Element} The read-only slots table
 */
export const Step2SlotsTable: FC<Step2SlotsTableProps> = ({ draftId }) => {
  const [queryState, fetchSlots] = useAtom(draftSlotsReadOnlyQueryLoadableAtom);

  const { triggerDownload: handleExport } = useBlobDownload({
    url: `/queries/packages/drafts/${draftId}/slots/export`,
    // Fallback only — useBlobDownload prefers the server's Content-Disposition
    // filename, which the controller sets to `draft-<id>-slot_allocations.csv`.
    filename: `draft-${draftId}-slot_allocations.csv`,
    body: { query: { selected: true } },
  });

  const loadData = useCallback(
    (params: TableState) => {
      fetchSlots({
        draftId,
        // `selected: true` is the read-only contract — baked in here, not
        // exposed as a prop, so callers can't accidentally widen the scope.
        selected: true,
        page: params.page,
        pageSize: params.pageSize,
        text: params.globalFilter || undefined,
        ...sortParamsFromState(params.sorting),
        ...draftSlotFilterStateToParams(params.columnFilters),
      });
    },
    [draftId, fetchSlots]
  );

  // DataTable doesn't self-fire `loadData` on mount in paginated mode;
  // kick off the initial fetch when the draft id changes. Match Step 1's
  // default-sort key so the read-only review opens in the same order.
  useEffect(() => {
    loadData({
      page: 1,
      pageSize: DEFAULT_PAGE_SIZE,
      globalFilter: '',
      sorting: [{ id: 'slotId', desc: false }],
      columnFilters: [],
      selection: { selectAll: false, toggledNodes: [] },
      expanded: {},
    });
  }, [draftId, loadData]);

  const data = queryState.state === 'hasData' ? queryState.data : null;
  const rows = useMemo<FlatSlotRow[]>(
    () => (data ? flattenDraftSlots(data.slots) : []),
    [data]
  );
  const totalCount = data?.meta.totalCount;
  const isLoading = queryState.state === 'loading';
  const errorMessage: string | null =
    queryState.state === 'hasError'
      ? queryState.error instanceof Error
        ? queryState.error.message
        : 'An unexpected error occurred'
      : null;

  return (
    <SlotTable
      rows={rows}
      totalCount={totalCount}
      isLoading={isLoading}
      loadData={loadData}
      errorMessage={errorMessage}
      onExport={handleExport}
    />
  );
};
